/**
 * Two scenarios, side by side: what differs in the assumptions, and what
 * that does to the answer (roadmap P-4).
 *
 * The comparison view runs every scenario; this reads two of those runs and
 * says what changes from A to B — which assumptions differ, how the counts
 * move, which priorities become achievable and which stop being achievable,
 * and whether each is affordable. Pure: no reads, no writes.
 *
 * Launch-calendar what-ifs have their own diff (`calendarChangeSet.ts`),
 * which compares launch placements in time; this one compares engine
 * feasibility, so the two do not overlap.
 */

import type { ScenarioRunResult } from './planningScenarioComparison';
import {
  PLANNING_MODE_LABELS,
  type PriorityFeasibility,
  type Scenario,
} from './planningEngineModule.types';
import type { FundingAffordability } from './timePhasedFinancial.types';

export interface AssumptionDifference {
  label: string;
  a: string;
  b: string;
}

export interface CountMove {
  label: string;
  a: number;
  b: number;
}

export interface PriorityMove {
  priorityId: string;
  name: string;
  a: PriorityFeasibility | null;
  b: PriorityFeasibility | null;
}

export interface ScenarioDiff {
  assumptions: AssumptionDifference[];
  counts: CountMove[];
  /** Fully achievable in B, not in A. */
  gained: PriorityMove[];
  /** Fully achievable in A, not in B. */
  lost: PriorityMove[];
  /** Any other change of feasibility, including in one run and not the other. */
  otherChanges: PriorityMove[];
}

const ACHIEVABLE: PriorityFeasibility = 'Fully Achievable';

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function assumptionRows(a: Scenario, b: Scenario): AssumptionDifference[] {
  const rows: [string, string, string][] = [
    ['Planning mode', PLANNING_MODE_LABELS[a.planningMode], PLANNING_MODE_LABELS[b.planningMode]],
    ['Horizon', `${a.planningWeeks} weeks`, `${b.planningWeeks} weeks`],
    ['Capacity buffer', pct(a.capacityBufferPercent), pct(b.capacityBufferPercent)],
    ['Uncertainty', a.uncertaintyBasis ?? 'buffer', b.uncertaintyBasis ?? 'buffer'],
    ['Hours per work unit', String(a.defaultWorkUnitHours), String(b.defaultWorkUnitHours)],
    ['Includes not-ready work', a.includeNotReady ? 'Yes' : 'No', b.includeNotReady ? 'Yes' : 'No'],
    ['Includes blocked work', a.includeBlocked ? 'Yes' : 'No', b.includeBlocked ? 'Yes' : 'No'],
  ];
  return rows.filter(([, x, y]) => x !== y).map(([label, x, y]) => ({ label, a: x, b: y }));
}

export function diffScenarioRuns(a: ScenarioRunResult, b: ScenarioRunResult): ScenarioDiff {
  const assumptions = assumptionRows(a.scenario, b.scenario);

  const ea = a.output?.executiveSummary;
  const eb = b.output?.executiveSummary;
  const out = (e: typeof ea) =>
    e ? e.blockedCount + e.notReadyCount + e.noCapacityModelCount : 0;
  const counts: CountMove[] =
    ea && eb
      ? [
          { label: 'In', a: ea.includedCount, b: eb.includedCount },
          { label: 'Out - capacity', a: ea.deferredCount, b: eb.deferredCount },
          { label: 'Out', a: out(ea), b: out(eb) },
          {
            label: 'Participation risks',
            a: ea.participationAtRiskCount + ea.participationBlockedCount,
            b: eb.participationAtRiskCount + eb.participationBlockedCount,
          },
        ]
      : [];

  const feasA = new Map<string, PriorityFeasibility>();
  const feasB = new Map<string, PriorityFeasibility>();
  const names = new Map<string, string>();
  const order: string[] = [];
  for (const [run, map] of [
    [a, feasA],
    [b, feasB],
  ] as const) {
    for (const ps of run.output?.prioritySummaries ?? []) {
      map.set(ps.priority.id, ps.feasibility);
      if (!names.has(ps.priority.id)) {
        names.set(ps.priority.id, ps.priority.name);
        order.push(ps.priority.id);
      }
    }
  }

  const gained: PriorityMove[] = [];
  const lost: PriorityMove[] = [];
  const otherChanges: PriorityMove[] = [];
  for (const id of order) {
    const fa = feasA.get(id) ?? null;
    const fb = feasB.get(id) ?? null;
    if (fa === fb) continue;
    const move = { priorityId: id, name: names.get(id) ?? id, a: fa, b: fb };
    if (fb === ACHIEVABLE) gained.push(move);
    else if (fa === ACHIEVABLE) lost.push(move);
    else otherChanges.push(move);
  }

  return { assumptions, counts, gained, lost, otherChanges };
}

/** One plain sentence on whether a scenario fits its funding, from the
 *  last launch-calendar run that priced it. */
export function affordabilitySentence(
  affordability: FundingAffordability | null,
  formatDollars: (n: number) => string,
): string {
  if (!affordability) return 'Not priced yet — run the Launch Calendar on this scenario';
  if (affordability.totalApproved <= 0) return 'No funding envelope set';
  if (affordability.withinEnvelope) {
    return `Within funding — ${formatDollars(affordability.totalForecastDemand)} of ${formatDollars(affordability.totalApproved)}`;
  }
  const periods = affordability.overloadedBuckets.length;
  return `Over funding by ${formatDollars(affordability.overageTotal)} in ${periods} period${periods === 1 ? '' : 's'}`;
}
